<template>
  <div v-if="statement" class="logic-statement">
    <div class="flex items-center">
      <multi-select
        v-model="statement.name"
        :options="statementOptions"
        :searchable="true"
        :can-clear="false"
        :groups="statementConditional || statementConditionalLicense"
        :placeholder="$t('logicStatement.name')"
        :error="errors?.missingStatement"
        :disabled="disabled"
        class="grow"
      />
      <RemoveStatement v-if="!disabled" class="remove-icon" @click="emit('remove')" />
    </div>
    <component
      :is="Statements[statementList?.[statement.name]?.type]"
      v-model="statement"
      :prototype="statementList?.[statement.name]"
      :errors="errors"
      :disabled="disabled"
    />
  </div>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import MultiSelect from '@utils/Multiselect.vue';
import RemoveStatement from '@images/remove_filter.svg?component';
import { computed, watch } from 'vue';
import Statements from '@components/utils/logicGate/statements';
import { useI18n } from 'vue-i18n';
import type { LogicStatement, MaybeHydra, FilterPrototypeList } from '@/entities/Entities';

interface Props {
  modelValue?: MaybeHydra<LogicStatement>;
  statementList?: FilterPrototypeList;
  errors?: object;
  disabled?: boolean;
}

const { t } = useI18n();

const props = defineProps<Props>();

const emit = defineEmits(['update:modelValue', 'remove']);
const statement = useVModel(props, 'modelValue', emit);

const rawStatementOptions = computed(() =>
  Object.keys(props.statementList ?? {}).map((item) => ({
    label: t(`logicStatement.${props.statementList?.[item]?.type}.${item}.DEFAULT`),
    value: item,
    conditional: props.statementList?.[item]?.declaration.conditional,
    conditionalLicense: props.statementList?.[item]?.declaration.conditionalLicense,
  })));

const statementConditional = computed(() => !(rawStatementOptions.value.filter((item) => item.conditional).length === 0));
const statementConditionalLicense = computed(() => !(rawStatementOptions.value.filter((item) => item.conditionalLicense).length === 0));

const statementOptions = computed(() => {
  if (!statementConditional.value && !statementConditionalLicense.value) {
    return rawStatementOptions.value;
  }
  return [
    ...(statementConditional.value ? [{
      label: t('logicStatement.dropdown.conditional'),
      options: rawStatementOptions.value.filter((item) => item.conditional),
    }] : []),
    ...(statementConditionalLicense.value ? [{
      label: t('logicStatement.dropdown.conditionalLicense'),
      options: rawStatementOptions.value.filter((item) => item.conditionalLicense),
    }] : []),
    {
      label: t('logicStatement.dropdown.unconditional'),
      options: rawStatementOptions.value.filter((item) => !item.conditional && !item.conditionalLicense),
    },
  ];
});

watch(() => statement.value?.name, (newVal, oldVal) => {
  if (newVal !== oldVal && statement.value) {
    statement.value.data = {};
  }
});
</script>

<style lang="scss" scoped>
.remove-icon {
  @apply ml-3;
  height: 1.5rem;
  width: 1.5rem;
  cursor: pointer;
}
</style>
